import { roundDecimal, sumMoney } from '@mms/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form } from 'react-bootstrap';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import type { Row } from '../api/types';
import { useCan } from '../auth/AuthContext';
import type { AnyForm } from '../forms/fields';
import { toId, toText } from '../forms/zod';
import { useBalances, type BalanceKind } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { useToast } from '../ui/feedback';
import { toOptions, useLookup } from '../ui/lookups';
import { FieldRenderer, invalidateData, type CrudConfig, type FieldDef } from './CrudScreen';

/**
 * The shared screen of the party masters (Sellers, Dispatchers, Agents), redesigned 2026-10-16: totals by currency along the top, one balance chip per currency in
 * the currency's own decimals, Status / "needs attention" filters, Deactivate / Activate and a safe Delete, and a form with Details and a Currencies section whose
 * rows have Currency, Opening balance, Direction and (for some) an optional limit. A currency used on another row is not offered again, a currency with
 * transactions is locked. The opening balance is stored signed (positive / negative by Direction), with 2 decimals whatever the currency's decimals are.
 */
export const STORED_DP = 2;
export const strip = (v: string) => v.replace(/,/g, '').trim();
export const fmtDp = (v: string, dp: number): string => {
  const s = strip(v);
  const neg = s.startsWith('-');
  const r = roundDecimal(s.replace(/^-/, ''), dp);
  return r === null ? s : `${neg ? '-' : ''}${r}`;
};
export const group = (v: string) => v.replace(/^(-?)(\d+)/, (_m, sign: string, int: string) => sign + int.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
const digits = (v: string) => v.replace(/\D/g, '');

interface CurrencyRow { currencyId: string; amount: string; direction: string; limit: string; locked: boolean }

export interface PartyOptions {
  title: string;
  /** lower case, singular: "seller" */
  singular: string;
  icon: string;
  menu: string;
  endpoint: string;
  /** the balances list (one row per party and currency) and the key that names the party in it; `lookup` is the active-parties dropdown list */
  balances: BalanceKind;
  lookup: string;
  balanceHeader: string;
  idKey: string;
  /** the first option is a positive balance, the second a negative one */
  directions: [{ value: string; label: string }, { value: string; label: string }];
  /** tooltips of a chip: a positive, a negative balance */
  tips: [string, string];
  totalsTitle: string;
  note: string;
  currenciesHelp: string;
  activeHelp: string;
  locationHelp: string;
  /** which currencies the form lists (besides being active) */
  eligible?: (c: Row) => boolean;
  /** an optional per-currency figure with a warning (the API field is `apiField`): `kind` says when a balance is flagged */
  extra?: {
    apiField: string;
    label: string;
    help: string;
    kind: 'low' | 'over';
    tip: (amount: string) => string;
    filter: { param: string; label: string };
  };
}

const makeSchema = (singular: string) => z.object({
  name: z.string().trim().min(1, 'Required').regex(/^[A-Za-z0-9 &.'-]{2,}$/, "Use letters, numbers, spaces and & . ' - only (at least 2 characters)"),
  location: z.string().trim().min(1, 'Required').regex(/^[A-Za-z ,.'-]+$/, 'Use letters, spaces, commas, hyphens and periods only'),
  mobileNo: z.string().trim().refine((v) => v === '' || (/^\+?[0-9 ]+$/.test(v) && digits(v).length >= 7 && digits(v).length <= 15), 'Enter 7 to 15 digits, with the country code'),
  email: z.string().trim().refine((v) => v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'Enter a valid email address'),
  isActive: z.boolean(),
  currencies: z.array(z.object({
    currencyId: z.string().min(1, 'Select the currency'),
    amount: z.string().trim().refine((v) => /^\d+(\.\d{1,4})?$/.test(strip(v)), 'Enter 0 or more'),
    direction: z.string(),
    limit: z.string().trim().refine((v) => v === '' || /^\d+(\.\d{1,4})?$/.test(strip(v)), 'Enter 0 or more'),
    locked: z.boolean(),
  })).min(1, `Add at least one currency to the ${singular}`),
});

export function partyMasterConfig(o: PartyOptions): CrudConfig {
  const [pos, neg] = o.directions;
  const x = o.extra;
  const key = (b: Row) => b[o.idKey] as number;

  /** Is this balance flagged by the currency's alert / limit? */
  const isFlagged = (balance: string, limit: unknown): boolean => {
    const l = Number(limit ?? 0);
    const b = Number(balance);
    if (!x || !(l > 0)) return false;
    return x.kind === 'low' ? b >= 0 && b < l : b > l;
  };

  /** Deactivate / Activate. The party is saved with PUT (the whole record), so the row is sent back as it is with only Active changed. */
  function useActions() {
    const qc = useQueryClient();
    const toast = useToast();
    const canEdit = useCan(o.menu, 'edit');
    const toggle = useMutation({
      mutationFn: (row: Row) => api.put(`${o.endpoint}/${row.id}`, {
        name: row.name, email: row.email ?? null, mobileNo: row.mobileNo ?? null, location: row.location ?? null, isActive: !row.isActive,
        currencies: (row.currencies as Row[]).map((c) => ({ currencyId: c.currencyId, openingBalance: String(c.openingBalance), ...(x ? { [x.apiField]: c[x.apiField] ?? null } : {}) })),
      }),
      onSuccess: async (_d, row) => {
        await invalidateData(qc);
        toast.success(row.isActive ? `${row.name} deactivated. Hidden from dropdowns, history is kept.` : `${row.name} activated`);
      },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not change the status'),
    });
    return (row: Row) => canEdit ? (
      <Button size="sm" variant="outline-secondary" className="me-1" disabled={toggle.isPending} onClick={() => toggle.mutate(row)}
        title={row.isActive ? 'Deactivate' : 'Activate'} aria-label={`${row.isActive ? 'Deactivate' : 'Activate'} ${row.name}`}>
        <i className={`bi ${row.isActive ? 'bi-pause-fill' : 'bi-play-fill'}`} aria-hidden />
      </Button>
    ) : null;
  }

  /** The total of the ACTIVE parties, one chip per currency. */
  function Totals() {
    const balances = useBalances(o.balances);
    const active = useLookup(o.lookup); // active parties only
    const currencies = useLookup('currencies', { all: 1 });
    const ids = new Set((active.data ?? []).map((s) => s.id));
    const dp = new Map((currencies.data ?? []).map((c) => [c.code as string, Number(c.decimals ?? 2)]));
    const sums = new Map<string, string[]>();
    for (const b of (balances.data ?? []) as Row[]) {
      if (!ids.has(key(b))) continue;
      sums.set(b.currencyCode, [...(sums.get(b.currencyCode) ?? []), String(b.balance)]);
    }
    const chips = [...sums.entries()].sort(([a], [b]) => a.localeCompare(b));
    return (
      <div className="mb-3" data-testid={`${o.singular}-totals`}>
        <div className="small text-body-secondary mb-1">{o.totalsTitle}</div>
        <div className="d-flex flex-wrap gap-2">
          {chips.map(([code, values]) => {
            const total = sumMoney(values, 3);
            return (
              <div key={code} className="rounded bg-body-tertiary px-3 py-2">
                <div className="small text-body-secondary">{code}</div>
                <strong className={Number(total) < 0 ? 'text-danger' : ''}>{group(fmtDp(total, dp.get(code) ?? 2))}</strong>
              </div>
            );
          })}
          {!balances.isPending && !active.isPending && chips.length === 0 && <span className="small text-body-secondary align-self-center">None.</span>}
        </div>
      </div>
    );
  }

  /** The Currencies section of the form. */
  function CurrencyRows({ form: anyForm, readOnly }: { form: AnyForm; readOnly: boolean }) {
    const form = anyForm as unknown as UseFormReturn<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
    const currencies = useLookup('currencies', { all: 1 });
    const { fields, append, remove } = useFieldArray({ control: form.control, name: 'currencies' });
    const rows = (form.watch('currencies') as CurrencyRow[] | undefined) ?? [];
    const all = currencies.data ?? [];
    const decimalsOf = (cid: string) => Number(all.find((c) => String(c.id) === cid)?.decimals ?? 2);
    const used = rows.map((r) => r.currencyId);
    const listable = (c: Row) => c.isActive !== false && (o.eligible?.(c) ?? true);
    const available = all.filter(listable);
    const optionsFor = (i: number) => all.filter((c) => String(c.id) === rows[i]?.currencyId || (listable(c) && !used.includes(String(c.id))));
    const errors = (form.formState.errors.currencies as Array<Record<string, { message?: string }> | undefined> | undefined) ?? [];
    const rootError = form.formState.errors.currencies as { message?: string; root?: { message?: string } } | undefined;
    const cols = x ? 'minmax(100px,1fr) minmax(120px,1.1fr) minmax(150px,1.2fr) minmax(110px,1fr) 34px' : 'minmax(110px,1fr) minmax(130px,1.2fr) minmax(150px,1.2fr) 34px';

    const reformat = (i: number) => {
      const r = (form.getValues('currencies') as CurrencyRow[])[i];
      if (!r || !r.currencyId) return;
      const dp = decimalsOf(r.currencyId);
      if (r.amount !== '') form.setValue(`currencies.${i}.amount`, fmtDp(r.amount, dp), { shouldDirty: true });
      if (r.limit !== '') form.setValue(`currencies.${i}.limit`, fmtDp(r.limit, dp), { shouldDirty: true });
    };

    return (
      <div className="card mb-3">
        <div className="card-header fw-semibold py-2 d-flex align-items-center">
          <span>Currencies <span className="text-danger">*</span></span>
          <span className="ms-auto small fw-normal text-body-secondary">{o.currenciesHelp}</span>
        </div>
        <div className="px-3 pt-2 small text-body-secondary d-none d-md-grid" style={{ gridTemplateColumns: cols, gap: 8 }}>
          <span>Currency</span><span>Opening balance</span><span>Direction</span>{x && <span>{x.label}</span>}<span />
        </div>
        <div className="px-3 pb-2">
          {fields.map((f, i) => {
            const locked = !!rows[i]?.locked;
            const rowErr = errors[i] ?? {};
            return (
              <div key={f.id} className="py-2 border-top" style={{ display: 'grid', gridTemplateColumns: cols, gap: 8, alignItems: 'start' }} data-testid={`currency-row-${i}`}>
                <div>
                  <Form.Select key={optionsFor(i).length} size="sm" aria-label={`Currency ${i + 1}`} disabled={readOnly || locked} isInvalid={!!rowErr.currencyId}
                    {...form.register(`currencies.${i}.currencyId`, { onChange: () => reformat(i) })}>
                    <option value="">Select…</option>
                    {toOptions(optionsFor(i), (c) => c.code as string).map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                  </Form.Select>
                  {rowErr.currencyId && <div className="text-danger small mt-1">{rowErr.currencyId.message}</div>}
                </div>
                <div>
                  <Form.Control size="sm" className="text-end" inputMode="decimal" placeholder="0.00" autoComplete="off" aria-label={`Opening balance ${i + 1}`}
                    disabled={readOnly || locked} isInvalid={!!rowErr.amount}
                    {...form.register(`currencies.${i}.amount`, { onBlur: () => reformat(i) })} />
                  <div className="small text-body-secondary mt-1">{locked ? 'Locked: has transactions' : 'Enter 0 if none'}</div>
                  {rowErr.amount && <div className="text-danger small">{rowErr.amount.message}</div>}
                </div>
                <div>
                  <Form.Select size="sm" aria-label={`Direction ${i + 1}`} disabled={readOnly || locked} {...form.register(`currencies.${i}.direction`)}>
                    <option value={pos.value}>{pos.label}</option>
                    <option value={neg.value}>{neg.label}</option>
                  </Form.Select>
                </div>
                {x && (
                  <div>
                    <Form.Control size="sm" className="text-end" inputMode="decimal" placeholder="Optional" autoComplete="off" aria-label={`${x.label} ${i + 1}`}
                      disabled={readOnly} isInvalid={!!rowErr.limit} {...form.register(`currencies.${i}.limit`, { onBlur: () => reformat(i) })} />
                    <div className="small text-body-secondary mt-1">{x.help}</div>
                    {rowErr.limit && <div className="text-danger small">{rowErr.limit.message}</div>}
                  </div>
                )}
                <Button variant="link" className="text-body-secondary p-0" aria-label={`Remove currency ${i + 1}`} disabled={readOnly || locked || fields.length === 1} onClick={() => remove(i)}
                  title={locked ? "Can't remove: has transactions" : fields.length === 1 ? 'At least one currency is required' : 'Remove'}>
                  <i className="bi bi-x-lg" aria-hidden />
                </Button>
              </div>
            );
          })}
          {rootError?.message && <div className="text-danger small">{rootError.message}</div>}
          {rootError?.root?.message && <div className="text-danger small">{rootError.root.message}</div>}
        </div>
        <div className="card-footer">
          <Button size="sm" variant="outline-secondary" disabled={readOnly || fields.length >= available.length}
            onClick={() => append({ currencyId: '', amount: '', direction: pos.value, limit: '', locked: false })}>
            <i className="bi bi-plus-lg me-1" aria-hidden />Add currency
          </Button>
        </div>
      </div>
    );
  }

  return {
    title: o.title, singular: o.singular, icon: o.icon, menu: o.menu, endpoint: o.endpoint, update: 'put', modalSize: 'lg',
    confirmDiscard: true,
    searchPlaceholder: 'Search name, mobile or ref',
    schema: makeSchema(o.singular),
    defaults: () => ({ name: '', location: '', mobileNo: '', email: '', isActive: true, currencies: [{ currencyId: '', amount: '', direction: pos.value, limit: '', locked: false }] }),
    toForm: (r) => ({
      name: r.name, location: r.location ?? '', mobileNo: r.mobileNo ?? '', email: r.email ?? '', isActive: r.isActive !== false,
      currencies: ((r.currencies as Row[] | undefined) ?? []).map((c) => {
        const opening = String(c.openingBalance);
        const dp = Number(c.decimals ?? 2);
        const lim = x && c[x.apiField] != null ? fmtDp(String(c[x.apiField]), dp) : '';
        return { currencyId: String(c.currencyId), amount: fmtDp(opening.replace(/^-/, ''), dp), direction: opening.startsWith('-') ? neg.value : pos.value, limit: lim, locked: !!c.hasTransactions };
      }),
    }),
    toPayload: (v) => ({
      name: v.name.trim(), location: toText(v.location), mobileNo: toText(v.mobileNo), email: toText(v.email), isActive: !!v.isActive,
      currencies: (v.currencies as CurrencyRow[]).map((c) => {
        const amount = fmtDp(c.amount, STORED_DP);
        return {
          currencyId: toId(c.currencyId), openingBalance: c.direction === neg.value && Number(amount) !== 0 ? `-${amount}` : amount,
          ...(x ? { [x.apiField]: c.limit.trim() === '' ? null : fmtDp(c.limit, STORED_DP) } : {}),
        };
      }),
    }),
    rowLabel: (r) => r.name,
    initialSort: { key: 'name', dir: 'asc' },
    useRowActions: useActions,
    useAbove: () => <Totals />,
    useBelow: () => <div className="small text-body-secondary mt-2">{o.note}</div>,
    useDeleteBlocked: () => {
      const balances = useBalances(o.balances);
      return (r) => (r.hasTransactions || ((balances.data ?? []) as Row[]).some((b) => key(b) === r.id && Number(b.balance) !== 0)
        ? 'Has transactions or a balance. Deactivate instead' : null);
    },
    useTable: () => {
      const balances = useBalances(o.balances);
      const currencies = useLookup('currencies', { all: 1 });
      const byParty = new Map<number, Row[]>();
      for (const b of (balances.data ?? []) as Row[]) byParty.set(key(b), [...(byParty.get(key(b)) ?? []), b]);
      const dp = new Map((currencies.data ?? []).map((c) => [c.code as string, Number(c.decimals ?? 2)]));
      const dim = (r: Row) => (r.isActive === false ? 'text-body-secondary' : '');
      const columns: Column[] = [
        { key: 'referenceNumber', header: 'Ref', sortKey: 'ref', className: 'text-nowrap', hideOnMobile: true, render: (r) => <span className={dim(r)}>{r.referenceNumber}</span> },
        { key: 'name', header: o.title.replace(/s$/, ''), sortKey: 'name', render: (r) => <><div className={`fw-semibold ${dim(r)}`}>{r.name}</div><div className="small text-body-secondary">{r.location ?? ''}</div></> },
        { key: 'mobileNo', header: 'Mobile', hideOnMobile: true, render: (r) => <span className={dim(r)}>{r.mobileNo || '—'}</span> },
        {
          key: 'balance', header: o.balanceHeader,
          render: (r) => balances.isPending ? (
            <span className="placeholder-glow"><span className="placeholder col-12" style={{ width: 90 }} /></span>
          ) : (
            <div>
              {(byParty.get(r.id) ?? []).map((b) => {
                const v = String(b.balance);
                const negative = Number(v) < 0;
                const lim = x ? ((r.currencies as Row[] | undefined) ?? []).find((c) => c.currencyId === b.currencyId)?.[x.apiField] : null;
                const warn = !negative && isFlagged(v, lim);
                const cls = negative ? 'text-bg-danger' : warn ? 'text-bg-warning' : 'text-bg-light border';
                const tip = negative ? o.tips[1] : warn && x ? x.tip(group(fmtDp(String(lim), dp.get(b.currencyCode) ?? 2))) : o.tips[0];
                return (
                  <span key={b.currencyId} className={`badge fw-normal me-1 ${cls}`} title={tip}>
                    {warn && <i className="bi bi-exclamation-triangle me-1" aria-hidden />}{b.currencyCode} {group(fmtDp(v, dp.get(b.currencyCode) ?? 2))}
                  </span>
                );
              })}
            </div>
          ),
        },
        { key: 'isActive', header: 'Status', render: (r) => <span className={`badge text-bg-${r.isActive === false ? 'secondary' : 'success'}`}>{r.isActive === false ? 'Inactive' : 'Active'}</span> },
      ];
      return {
        columns,
        filters: [
          { param: 'currencyId', label: 'Currency', options: toOptions(currencies.data?.filter((c) => c.isActive !== false), (c) => c.code as string) },
          { param: 'isActive', label: 'Status', allLabel: 'All statuses', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
          { param: x ? x.filter.param : 'withBalance', label: x ? x.filter.label : 'With balance only', type: 'checkbox' as const, value: 'true', options: [] },
        ],
      };
    },
    useForm: (form, { readOnly }) => {
      const field = (def: FieldDef, col: number) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
      const body = (
        <>
          <div className="card mb-3">
            <div className="card-header fw-semibold py-2">{o.title.replace(/s$/, '')} details</div>
            <div className="card-body pb-0">
              <div className="row">
                {field({ name: 'name', label: 'Name', type: 'text', required: true }, 6)}
                {field({ name: 'location', label: 'Location', type: 'text', required: true, help: o.locationHelp }, 6)}
                {field({ name: 'mobileNo', label: 'Mobile', type: 'text', placeholder: '+968 91234567', help: 'With country code' }, 4)}
                {field({ name: 'email', label: 'Email', type: 'text' }, 4)}
                {field({ name: 'isActive', label: 'Active', type: 'checkbox', help: o.activeHelp }, 4)}
              </div>
            </div>
          </div>
          <CurrencyRows form={form} readOnly={readOnly} />
        </>
      );
      return { body, fields: [] };
    },
  };
}
